import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, DeleteModal } from '@app/components/shared/Modal';
import { Snackbar, useSnackbar } from '@app/components/shared/Snackbar';
import { AdminHeader } from '@app/components/shared/AdminHeader';
import { EditCustomerModal } from '@app/components/shared/EditCustomerModal';
import { initializeSalesforceConfig, getSalesforceUrl, isSalesforceConfigInitialized } from '@shared/utils/salesforceUtils';

interface Customer {
  id: number;
  sfid?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email: string;
  phone?: string | null;
  eventCount: number;
  upcomingEventDate?: string | null;
  createdDate: string | null;
  systemModStamp: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface CustomerListProps {
  onLogout: () => void;
}

export function CustomerList({ onLogout }: CustomerListProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('systemModStamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean; customer?: Customer }>({ show: false });
  const [salesforceConfigLoaded, setSalesforceConfigLoaded] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 5,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const navigate = useNavigate();

  const fetchCustomers = async (page: number = pagination.currentPage, search: string = searchTerm, sort: string = sortBy, direction: string = sortDirection) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        search: search.trim(),
        sortBy: sort,
        sortDirection: direction,
      });
      
      const response = await fetch(`/api/admin/customers?${params.toString()}`, {
        credentials: 'include',
      });

      if (response.status === 401) {
        onLogout();
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.success) {
        setCustomers(result.data);
        setPagination(result.pagination);
      } else {
        setError(result.error || 'Failed to fetch customers');
      }
    } catch (err) {
      console.error('Fetch customers error:', err);
      setError(err instanceof Error ? err.message : 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Initial load and Salesforce config
  useEffect(() => {
    fetchCustomers();
    
    // Initialize Salesforce config
    initializeSalesforceConfig().then(() => {
      setSalesforceConfigLoaded(true);
    });
  }, []);

  // Fetch customers when search term changes (debounced)
  useEffect(() => {
    if (searchTerm === '') return; // Skip on initial load when searchTerm is empty
    
    const timeoutId = setTimeout(() => {
      fetchCustomers(1, searchTerm, sortBy, sortDirection);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Fetch customers when sort parameters change
  useEffect(() => {
    if (searchTerm === '' && sortBy === 'systemModStamp' && sortDirection === 'desc') {
      return; // Skip on initial load when all values are at their initial state
    }
    fetchCustomers(1, searchTerm, sortBy, sortDirection);
  }, [sortBy, sortDirection]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchCustomers(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.hasPreviousPage) {
      handlePageChange(pagination.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      handlePageChange(pagination.currentPage + 1);
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    setIsDeletingCustomer(true);
    try {
      const response = await fetch(`/api/admin/customers/${customer.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.status === 401) {
        onLogout();
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.success) {
        setCustomers(customers.filter(c => c.id !== customer.id));
        setShowDeleteModal({ show: false });
        showSnackbar('Customer deleted successfully!', 'success');
      } else {
        showSnackbar(result.error || 'Failed to delete customer', 'error');
      }
    } catch (err) {
      console.error('Delete customer error:', err);
      showSnackbar(err instanceof Error ? err.message : 'Network error occurred', 'error');
    } finally {
      setIsDeletingCustomer(false);
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader onLogout={onLogout} />

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg">
          {/* Title and Controls */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Customers List</h1>
            
            {/* Search - Full Width */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search:</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name or last name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Second Row: Add Customer and Sort By */}
            <div className="flex justify-between items-center">
              {/* Add Customer Button */}
              <button 
                onClick={() => setShowAddCustomerModal(true)}
                className="bg-black text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer"
              >
                + ADD A NEW CUSTOMER
              </button>

              {/* Sort By */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {/* TODO: Fix the sort by Upcoming event date */}
                  <option value="systemModStamp">Upcoming event date</option>
                  <option value="name">Name</option>
                  <option value="created_date">Created date</option>
                </select>
                
                {/* Sort Direction Toggle */}
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-50 cursor-pointer"
                  title={`Currently sorting ${sortDirection === 'asc' ? 'ascending' : 'descending'}. Click to ${sortDirection === 'asc' ? 'descend' : 'ascend'}.`}
                >
                  {sortDirection === 'asc' ? (
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Customer List */}
          <div className="space-y-4 p-6">
            {customers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No customers found</p>
              </div>
            ) : (
              customers.map((customer) => (
                <div 
                  key={customer.id} 
                  className="border border-gray-300 border-l-4 border-l-gray-400 rounded-lg bg-white p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/customers/${customer.id}/events`)}
                >
                  {/* First Row: Customer Name and Event Count */}
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-sm">
                          {((customer.firstName && customer.firstName.trim()) ? customer.firstName.trim()[0] : 
                           (customer.name && customer.name.trim()) ? customer.name.trim()[0] : '?').toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        Customer: {customer.name || `${customer.firstName} ${customer.lastName}`}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {customer.eventCount || 0} Events
                    </div>
                  </div>

                  {/* Second Row: Email and Phone */}
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500">
                      Email: {customer.email}
                    </span>
                    <span className="text-sm text-gray-500">
                      Phone: {customer.phone || 'Not provided'}
                    </span>
                  </div>

                  {/* Third Row: Salesforce Link */}
                  <div className="flex justify-end items-center mb-3">
                    <div>
                      {customer.sfid && salesforceConfigLoaded ? (
                        <a
                          href={getSalesforceUrl(customer.sfid)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Salesforce link
                        </a>
                      ) : customer.sfid ? (
                        <span className="text-gray-400 text-sm">Loading...</span>
                      ) : (
                        <span className="text-gray-400 text-sm">Salesforce link</span>
                      )}
                    </div>
                  </div>

                  {/* Fourth Row: Action Buttons (with border separator) */}
                  <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                    <div className="flex items-center space-x-6">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCustomer(customer);
                        }}
                        className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 cursor-pointer"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span className="text-sm">Edit customer</span>
                      </button>

                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteModal({ show: true, customer });
                        }}
                        className="flex items-center space-x-1 text-gray-600 hover:text-red-600 cursor-pointer"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span className="text-sm">Delete customer</span>
                      </button>
                    </div>

                    <button
                      className="px-4 py-1 rounded text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer"
                    >
                      VIEW EVENTS
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount} results
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={handlePreviousPage}
                    disabled={!pagination.hasPreviousPage}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => {
                    if (
                      pageNum === 1 || 
                      pageNum === pagination.totalPages || 
                      (pageNum >= pagination.currentPage - 1 && pageNum <= pagination.currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 text-sm rounded cursor-pointer ${
                            pageNum === pagination.currentPage
                              ? 'bg-black text-white'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === pagination.currentPage - 2 || 
                      pageNum === pagination.currentPage + 2
                    ) {
                      return <span key={pageNum} className="px-2 text-sm text-gray-500">...</span>;
                    }
                    return null;
                  })}
                  
                  <button 
                    onClick={handleNextPage}
                    disabled={!pagination.hasNextPage}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal.show && !!showDeleteModal.customer}
        onClose={() => setShowDeleteModal({ show: false })}
        onConfirm={() => showDeleteModal.customer && handleDeleteCustomer(showDeleteModal.customer)}
        title="Do you want to delete this customer?"
        message="This action will permanently delete the customer and all associated events. This cannot be undone."
        isDeleting={isDeletingCustomer}
      />

      {/* Add Customer Modal */}
      <Modal 
        isOpen={showAddCustomerModal} 
        onClose={() => {
          setShowAddCustomerModal(false);
          setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
        }}
      >
        <h3 className="text-lg font-semibold text-gray-900 text-center mb-4">
          Add New Customer
        </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  value={newCustomer.firstName}
                  onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="First name"
                  disabled={isCreatingCustomer}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  value={newCustomer.lastName}
                  onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Last name"
                  disabled={isCreatingCustomer}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@example.com"
                  disabled={isCreatingCustomer}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                  disabled={isCreatingCustomer}
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={async () => {
                  // Basic validation
                  if (!newCustomer.firstName.trim() || !newCustomer.lastName.trim() || !newCustomer.email.trim()) {
                    showSnackbar('Please fill in all required fields', 'error');
                    return;
                  }
                  
                  // Email validation
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(newCustomer.email)) {
                    showSnackbar('Please enter a valid email address', 'error');
                    return;
                  }
                  
                  setIsCreatingCustomer(true);
                  
                  try {
                    // Check if email already exists
                    const checkResponse = await fetch(`/api/admin/customers/check-email?email=${encodeURIComponent(newCustomer.email)}`, {
                      credentials: 'include',
                    });
                    
                    if (!checkResponse.ok) {
                      throw new Error('Failed to check email availability');
                    }
                    
                    const checkResult = await checkResponse.json();
                    if (checkResult.exists) {
                      showSnackbar('A customer with this email already exists', 'error');
                      setIsCreatingCustomer(false);
                      return;
                    }
                    
                    // Create the customer
                    const response = await fetch('/api/admin/customers', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        firstName: newCustomer.firstName.trim(),
                        lastName: newCustomer.lastName.trim(),
                        email: newCustomer.email.trim(),
                        phone: newCustomer.phone.trim() || null
                      }),
                    });

                    const result = await response.json();
                    if (result.success) {
                      showSnackbar('Customer created successfully!', 'success');
                      setShowAddCustomerModal(false);
                      setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                      fetchCustomers(1); // Refresh from first page
                    } else {
                      showSnackbar(result.error || 'Failed to create customer', 'error');
                    }
                  } catch (err) {
                    showSnackbar('Network error occurred', 'error');
                  } finally {
                    setIsCreatingCustomer(false);
                  }
                }}
                className="flex-1 bg-black text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer flex items-center justify-center"
                disabled={!newCustomer.firstName.trim() || !newCustomer.lastName.trim() || !newCustomer.email.trim() || isCreatingCustomer}
              >
                {isCreatingCustomer ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Add Customer'
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddCustomerModal(false);
                  setNewCustomer({ firstName: '', lastName: '', email: '', phone: '' });
                }}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer"
                disabled={isCreatingCustomer}
              >
                Cancel
              </button>
        </div>
      </Modal>

      {/* Edit Customer Modal */}
      <EditCustomerModal
        isOpen={!!editingCustomer}
        customer={editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSuccess={(updatedCustomer) => {
          // Update the customer in the local state, preserving fields that aren't updated
          setCustomers(customers.map(c => 
            c.id === updatedCustomer.id 
              ? { 
                  ...c, 
                  ...updatedCustomer, 
                  eventCount: c.eventCount, // Preserve eventCount
                  upcomingEventDate: c.upcomingEventDate // Preserve upcomingEventDate 
                } 
              : c
          ));
          setEditingCustomer(null);
        }}
        onLogout={onLogout}
        showSnackbar={showSnackbar}
      />
      
      {/* Snackbar */}
      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        isVisible={snackbar.isVisible}
        onClose={hideSnackbar}
      />
    </div>
  );
}